import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { signInWithCustomToken } from "firebase/auth";
import { httpsCallable } from "firebase/functions";
import { FaUser, FaEnvelope, FaPhone, FaUserTie, FaBuilding, FaHardHat } from "react-icons/fa";
import { auth, functions } from "../firebase";
import { useToast } from "../components/Toast";
import {
  colors,
  spacing,
  typography,
  radius,
} from "../utils/designTokens";

/**
 * Pantalla publica /solicitar-demo: formulario que captura nombre, email, rol y
 * telefono del visitante, llama a la callable `solicitarDemo` y lo loguea
 * automaticamente con `signInWithCustomToken` en el modo demo (residente
 * read-only del edificio "Residencial Obligado (Demo)").
 *
 * Flujo:
 *   1. Form -> validacion liviana client-side -> llamada al callable.
 *   2. Si la response trae { existingUser: true } -> redirect a /login.
 *   3. Si trae { customToken } -> signInWithCustomToken -> getIdToken(true) ->
 *      forzamos refresh para que los custom claims ya esten en el primer ID
 *      token -> redirect a "/".
 */

const ROLES = [
  { id: "residente", label: "Soy residente de un edificio", icon: FaUser },
  { id: "admin_edificio", label: "Administro un edificio", icon: FaUserTie },
  { id: "desarrollador", label: "Desarrollo edificios", icon: FaHardHat },
];

// Regex livianos (espejo del server).
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE_VISIBLE_RE = /^[+\d\s\-()]{7,}$/;

const SolicitarDemo = () => {
  const navigate = useNavigate();
  const { showToast } = useToast();

  const [form, setForm] = useState({
    name: "",
    email: "",
    rol: "",
    phone: "",
    consent: false,
  });
  const [submitting, setSubmitting] = useState(false);
  const [step, setStep] = useState("form"); // form | loading | done

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const validate = () => {
    if (!form.name.trim() || form.name.trim().length < 2) {
      return "Decinos tu nombre.";
    }
    if (!EMAIL_RE.test(form.email.trim())) {
      return "Email invalido.";
    }
    if (!form.rol) {
      return "Elegi una opcion que mejor te describa.";
    }
    if (!PHONE_VISIBLE_RE.test(form.phone.trim())) {
      return "Telefono invalido (formato +598XXXXXXXX).";
    }
    if (!form.consent) {
      return "Necesitamos tu consentimiento para tratar tus datos.";
    }
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    const err = validate();
    if (err) {
      showToast(err, "error");
      return;
    }

    setSubmitting(true);
    setStep("loading");

    try {
      // Atribucion que viene de habitia.ai (getDemoUrl anexa utm_*/fbclid/lp
      // al querystring): la reenviamos a la callable para que quede en el lead.
      // Sin esto el salto cross-domain sitio→app rompia la cadena de atribucion.
      const atribucion = (() => {
        try {
          const p = new URLSearchParams(window.location.search);
          const utm = {};
          ["source", "medium", "campaign", "content", "term"].forEach((k) => {
            const v = p.get(`utm_${k}`);
            if (v) utm[k] = v.slice(0, 120);
          });
          return {
            ...(Object.keys(utm).length ? { utm } : {}),
            ...(p.get("fbclid") ? { fbclid: p.get("fbclid").slice(0, 500) } : {}),
            ...(p.get("lp") ? { landingPage: p.get("lp").slice(0, 200) } : {}),
          };
        } catch (e) {
          return {};
        }
      })();

      const fn = httpsCallable(functions, "solicitarDemo");
      const res = await fn({
        name: form.name.trim(),
        email: form.email.trim().toLowerCase(),
        rol: form.rol,
        phone: form.phone.trim(),
        consent: true,
        ...atribucion,
      });
      const data = res.data || {};

      // Caso: ya tiene cuenta real -> login normal
      if (data.existingUser) {
        showToast(data.message || "Ya tenes cuenta. Ingresa con tu email.", "info");
        setStep("form");
        setSubmitting(false);
        navigate(data.redirect || "/");
        return;
      }

      // Caso: tenemos custom token -> sign in y redirigir
      if (!data.customToken) {
        throw new Error("Respuesta invalida del servidor.");
      }

      await signInWithCustomToken(auth, data.customToken);
      // Forzamos refresh del ID token para que los custom claims `habitia_demo`
      // viajen desde el primer request (sin esto el WS de Habi puede no verlos).
      if (auth.currentUser) {
        await auth.currentUser.getIdToken(true);
      }
      setStep("done");
      showToast(data.message || `Bienvenido ${form.name}!`, "success");
      // Redirect al home residente
      navigate(data.redirect || "/", { replace: true });
    } catch (err) {
      console.error("solicitarDemo error:", err);
      const msg = err?.message || "No pudimos crear tu demo. Probá de nuevo.";
      showToast(msg, "error");
      setStep("form");
    } finally {
      setSubmitting(false);
    }
  };

  if (step === "loading" || step === "done") {
    return (
      <div style={pageWrapper}>
        <div style={cardStyle}>
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: spacing[4] }}>
            <div
              style={{
                width: 48,
                height: 48,
                border: `4px solid ${colors.gray200}`,
                borderTopColor: colors.primary,
                borderRadius: "50%",
                animation: "spin 1s linear infinite",
              }}
            />
            <div style={{ textAlign: "center" }}>
              <h2 style={{ ...titleStyle, fontSize: typography.sizes.xl }}>Preparando tu demo</h2>
              <p style={{ ...subtitleStyle, marginTop: 8 }}>
                Creamos tu acceso al edificio de muestra. Esto toma unos segundos.
              </p>
            </div>
          </div>
        </div>
        <style>{"@keyframes spin { to { transform: rotate(360deg); } }"}</style>
      </div>
    );
  }

  return (
    <div style={pageWrapper}>
      <form onSubmit={handleSubmit} style={cardStyle}>
        {/* Cabecera */}
        <div style={{ textAlign: "center", marginBottom: spacing[5] }}>
          <h1 style={titleStyle}>Probá Habitia</h1>
          <p style={subtitleStyle}>
            Vas a entrar a un edificio de muestra como residente para ver la plataforma en uso. Solo necesitamos unos datos para personalizar tu acceso.
          </p>
        </div>

        {/* Nombre */}
        <Field label="Tu nombre" htmlFor="name" required>
          <div style={inputWrap}>
            <FaUser size={14} color={colors.gray600} style={inputIcon} />
            <input
              id="name"
              type="text"
              value={form.name}
              onChange={(e) => handleChange("name", e.target.value)}
              placeholder="Ej: Juan Perez"
              required
              autoFocus
              style={inputStyle}
            />
          </div>
        </Field>

        {/* Email */}
        <Field label="Email" htmlFor="email" required>
          <div style={inputWrap}>
            <FaEnvelope size={14} color={colors.gray600} style={inputIcon} />
            <input
              id="email"
              type="email"
              value={form.email}
              onChange={(e) => handleChange("email", e.target.value)}
              placeholder="vos@ejemplo.com"
              required
              style={inputStyle}
            />
          </div>
        </Field>

        {/* Telefono */}
        <Field label="Telefono" htmlFor="phone" required hint="Formato Uruguay (+598)">
          <div style={inputWrap}>
            <FaPhone size={14} color={colors.gray600} style={inputIcon} />
            <input
              id="phone"
              type="tel"
              value={form.phone}
              onChange={(e) => handleChange("phone", e.target.value)}
              placeholder="+59899123456"
              required
              style={inputStyle}
            />
          </div>
        </Field>

        {/* Rol */}
        <Field label="Que mejor te describe?" required>
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {ROLES.map((r) => {
              const Icon = r.icon;
              const active = form.rol === r.id;
              return (
                <label
                  key={r.id}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 12,
                    padding: spacing[3],
                    border: `1px solid ${active ? colors.primary : colors.gray300}`,
                    borderRadius: radius.sm,
                    cursor: "pointer",
                    background: active ? colors.primaryLighter : colors.white,
                    transition: "all 0.15s",
                  }}
                >
                  <input
                    type="radio"
                    name="rol"
                    value={r.id}
                    checked={active}
                    onChange={() => handleChange("rol", r.id)}
                    style={{ accentColor: colors.primary }}
                  />
                  <Icon size={16} color={active ? colors.primary : colors.gray600} />
                  <span style={{ color: colors.gray900, fontSize: typography.sizes.base, fontWeight: 400 }}>
                    {r.label}
                  </span>
                </label>
              );
            })}
          </div>
        </Field>

        {/* Consent */}
        <label
          style={{
            display: "flex",
            alignItems: "flex-start",
            gap: 8,
            marginBottom: spacing[4],
            cursor: "pointer",
            fontSize: typography.sizes.sm,
            color: colors.gray700,
          }}
        >
          <input
            type="checkbox"
            checked={form.consent}
            onChange={(e) => handleChange("consent", e.target.checked)}
            style={{ marginTop: 3, accentColor: colors.primary }}
          />
          <span>
            Acepto el tratamiento de mis datos para fines comerciales segun la{" "}
            <a
              href="/politica-de-privacidad"
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: colors.primary, textDecoration: "underline" }}
            >
              Politica de Privacidad
            </a>{" "}
            (Ley 18.331).
          </span>
        </label>

        {/* Submit */}
        <button
          type="submit"
          disabled={submitting}
          style={{
            width: "100%",
            padding: "14px 20px",
            background: colors.primary,
            color: colors.white,
            border: "none",
            borderRadius: radius.sm,
            fontSize: typography.sizes.lg,
            fontWeight: 400,
            fontFamily: "inherit",
            cursor: submitting ? "wait" : "pointer",
            opacity: submitting ? 0.6 : 1,
            transition: "opacity 0.15s",
          }}
        >
          {submitting ? "Preparando tu demo..." : "Entrar al demo"}
        </button>

        {/* Footer */}
        <p
          style={{
            marginTop: spacing[4],
            textAlign: "center",
            fontSize: typography.sizes.sm,
            color: colors.gray600,
          }}
        >
          <FaBuilding size={11} style={{ marginRight: 4 }} />
          Vas a ver el "Residencial Obligado (Demo)" como residente de muestra.
        </p>
      </form>
    </div>
  );
};

// Subcomponentes locales (no usamos FormField global porque esta pantalla es publica
// y queremos minimizar dependencias del shell de la app).
const Field = ({ label, htmlFor, hint, required, children }) => (
  <div style={{ marginBottom: spacing[4] }}>
    <label
      htmlFor={htmlFor}
      style={{
        display: "block",
        fontSize: typography.sizes.sm,
        color: colors.gray700,
        marginBottom: 6,
        fontWeight: 400,
      }}
    >
      {label}
      {required && <span style={{ color: colors.error, marginLeft: 4 }}>*</span>}
    </label>
    {children}
    {hint && (
      <div style={{ marginTop: 4, fontSize: typography.sizes.sm, color: colors.gray600 }}>
        {hint}
      </div>
    )}
  </div>
);

// Estilos
const pageWrapper = {
  minHeight: "100vh",
  background: `linear-gradient(135deg, ${colors.primarySubtle} 0%, ${colors.white} 100%)`,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: spacing[4],
  fontFamily: "inherit",
};

const cardStyle = {
  background: colors.white,
  borderRadius: radius.md,
  padding: spacing[6] || spacing[5],
  boxShadow: "0 4px 24px rgba(0, 0, 0, 0.08)",
  maxWidth: 480,
  width: "100%",
};

const titleStyle = {
  fontSize: typography.sizes["2xl"] || typography.sizes.xl,
  fontWeight: 400,
  color: colors.gray900,
  margin: 0,
};

const subtitleStyle = {
  fontSize: typography.sizes.base,
  color: colors.gray700,
  margin: "8px 0 0",
  fontWeight: 400,
  lineHeight: 1.5,
};

const inputWrap = {
  position: "relative",
  display: "flex",
  alignItems: "center",
};

const inputIcon = {
  position: "absolute",
  left: 12,
  pointerEvents: "none",
};

const inputStyle = {
  width: "100%",
  padding: "12px 12px 12px 36px",
  fontSize: typography.sizes.base,
  fontWeight: 400,
  fontFamily: "inherit",
  border: `1px solid ${colors.gray300}`,
  borderRadius: radius.sm,
  outline: "none",
  color: colors.gray900,
  background: colors.white,
};

export default SolicitarDemo;
